// src/utils/funnel.js
//
// First-party journey tracking for the admin "User Journey" view
// (landing -> sign up -> OTP -> dashboard). Every event carries a persistent
// per-browser device ID (`hv_did`), so an anonymous visit can be tied to the
// user it later becomes.
//
// The ID lives in a cookie scoped to .hervest.ng so the landing site and this
// app share it, with localStorage as a backup (and the cookie restores it if
// localStorage.clear() runs - this app calls that in a few places).
//
// Everything here is best-effort: a tracking failure must never break signup.

import { Base_URL } from "../features/utils/constant";
import { getAttribution } from "./attribution";

const DEVICE_COOKIE = "hv_did";
const SESSION_KEY = "hv_sid";
const FIRST_TOUCH_KEY = "hv_first_touch";
const FLUSH_DELAY_MS = 1500;
const MAX_BATCH = 20;
const TWO_YEARS_SECONDS = 60 * 60 * 24 * 365 * 2;

let queue = [];
let flushTimer = null;

const newId = () => {
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
};

const cookieDomain = () => {
  const host = window.location.hostname;
  return host === "hervest.ng" || host.endsWith(".hervest.ng")
    ? "; domain=.hervest.ng"
    : "";
};

const readCookie = (name) => {
  const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
  return match ? decodeURIComponent(match[1]) : null;
};

const writeCookie = (name, value) => {
  const secure = window.location.protocol === "https:" ? "; Secure" : "";
  document.cookie = `${name}=${encodeURIComponent(
    value
  )}; path=/; max-age=${TWO_YEARS_SECONDS}; SameSite=Lax${cookieDomain()}${secure}`;
};

export const getDeviceId = () => {
  if (typeof window === "undefined") return null;
  try {
    let id = readCookie(DEVICE_COOKIE);
    if (!id) {
      try {
        id = window.localStorage.getItem(DEVICE_COOKIE);
      } catch (e) {
        /* storage blocked - fall through to a fresh id */
      }
    }
    if (!id) id = newId();

    // Re-assert both stores every call so whichever one survived heals the other.
    writeCookie(DEVICE_COOKIE, id);
    try {
      window.localStorage.setItem(DEVICE_COOKIE, id);
    } catch (e) {
      /* ignore */
    }
    return id;
  } catch (e) {
    return null;
  }
};

const getSessionId = () => {
  try {
    let id = window.sessionStorage.getItem(SESSION_KEY);
    if (!id) {
      id = newId();
      window.sessionStorage.setItem(SESSION_KEY, id);
    }
    return id;
  } catch (e) {
    return null;
  }
};

// document.referrer / entry URL only describe the first load of the browser,
// so remember them once; the backend keeps just the first-touch values anyway.
const getFirstTouch = () => {
  try {
    const raw = window.localStorage.getItem(FIRST_TOUCH_KEY);
    if (raw) return JSON.parse(raw);
    const first = {
      referrer: document.referrer || null,
      landing_page: window.location.href,
    };
    window.localStorage.setItem(FIRST_TOUCH_KEY, JSON.stringify(first));
    return first;
  } catch (e) {
    return { referrer: document.referrer || null, landing_page: window.location.href };
  }
};

const endpoint = () => `${(Base_URL || "").replace(/\/?$/, "/")}funnel/events`;

const flush = () => {
  flushTimer = null;
  if (!queue.length) return;

  const deviceId = getDeviceId();
  if (!deviceId) {
    queue = [];
    return;
  }

  const batch = queue.splice(0, MAX_BATCH);
  const firstTouch = getFirstTouch();
  const attribution = getAttribution();
  const body = JSON.stringify({
    device_id: deviceId,
    session_id: getSessionId(),
    platform: "web",
    referrer: firstTouch.referrer || undefined,
    landing_page: firstTouch.landing_page || undefined,
    attribution: attribution || undefined,
    events: batch,
  });

  try {
    // keepalive lets the request survive a page unload (e.g. the navigation
    // right after clicking submit). Plain fetch, not APICall: no toasts, no
    // auth header, no 401 redirect side effects.
    fetch(endpoint(), {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body,
      keepalive: true,
    }).catch(() => {});
  } catch (e) {
    /* never throw from tracking */
  }

  if (queue.length) scheduleFlush(0);
};

const scheduleFlush = (delay = FLUSH_DELAY_MS) => {
  if (flushTimer) return;
  flushTimer = setTimeout(flush, delay);
};

// Routes whose visit is already logged as a named milestone event
// (login_view, dashboard_view, ...), so a generic page_view there is a duplicate.
const MILESTONE_ROUTES = ["/", "/dashboard", "/onboarding", "/welcome", "/register"];

// Logged once per browser session: repeats add volume but no insight.
const ONCE_PER_SESSION = [
  "login_view",
  "dashboard_view",
  "onboarding_view",
  "welcome_view",
  "sign_up_visited",
  "app_banner_shown",
];

const DEDUPE_WINDOW_MS = 1000;
let lastKey = null;
let lastAt = 0;

const shouldTrack = (event, route) => {
  // Signed-in browsing is product usage, not signup funnel: keep named
  // events (logins, banner clicks, ...) but skip the per-page noise.
  if (event === "page_view") {
    if (MILESTONE_ROUTES.includes(route)) return false;
    try {
      if (window.localStorage.getItem("token")) return false;
    } catch (e) {
      /* storage blocked - fall through */
    }
  }

  // Same event + route fired twice back to back (StrictMode, double effects).
  const key = `${event}|${route}`;
  const now = Date.now();
  if (key === lastKey && now - lastAt < DEDUPE_WINDOW_MS) return false;
  lastKey = key;
  lastAt = now;

  if (ONCE_PER_SESSION.includes(event)) {
    try {
      const storeKey = `hv_once_${event}`;
      if (window.sessionStorage.getItem(storeKey)) return false;
      window.sessionStorage.setItem(storeKey, "1");
    } catch (e) {
      /* storage blocked - better to over-report than lose the event */
    }
  }
  return true;
};

export const trackFunnel = (event, { step, route, props } = {}) => {
  if (typeof window === "undefined" || !event) return;
  try {
    if (!shouldTrack(event, route || window.location.pathname)) return;
    queue.push({
      event,
      step,
      route: route || window.location.pathname,
      ts: Date.now(),
      props,
    });
    scheduleFlush();
  } catch (e) {
    /* ignore */
  }
};

// Called after a successful login so a returning customer on a new device
// joins their existing journey.
export const identifyDevice = () => {
  const token = window.localStorage.getItem("token");
  const deviceId = getDeviceId();
  if (!token || !deviceId) return;
  try {
    fetch(`${(Base_URL || "").replace(/\/?$/, "/")}funnel/identify`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ device_id: deviceId }),
      keepalive: true,
    }).catch(() => {});
  } catch (e) {
    /* ignore */
  }
};

if (typeof window !== "undefined") {
  // Flush whatever is queued when the tab is hidden or closed.
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") flush();
  });
  window.addEventListener("pagehide", flush);
}
